import { usePrivateWorkspace } from "@/api/workspaces";
import { MessageInput, MessageInputRef } from "@/components/MessageInput";
import { MessageInputControlSet } from "@/constants/message-input";
import { ROUTES } from "@/constants/routes";
import { useSessionContext } from "@/context/SessionContext";
import { ComposerAttachDropTarget } from "@/dnd/ComposerAttachDropTarget";
import { useRegisterOsFileDrop } from "@/dnd/OsFileDropLayer";
import { useRegisterComposerAttachTarget } from "@/dnd/TransferDndProvider";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useMessageActions } from "@/hooks/useMessageActions";
import { cn } from "@/utils";
import * as analytics from "@/utils/analytics";
import { route } from "@/utils/route";
import type { MetadataIcon } from "@cohesive/api-client";
import { PostMessageParams, ReferencedEntity } from "@cohesive/api-client";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { WorkspaceDropZone } from "../components/WorkspaceDropZone";
import { ComposerScopeChip } from "./ComposerScopeChip";

/**
 * Longer than ComposerShell's 150ms transition, short enough that the first
 * real hover can't realistically beat it.
 */
const MOUNT_SETTLE_MS = 250;

/**
 * False for the first moments after mount, then true forever.
 *
 * `autoFocus` reaches the editor through Lexical's plugin, which focuses in an
 * effect — i.e. *after* the first paint. So the composer paints its resting
 * border, then focus lands and ComposerShell's
 * `transition-[border-color,box-shadow]` animates it up to the focused border
 * and glow. Navigating between workspaces remounts the surface by design (the
 * `key` that resets the chat session), so that 150ms brighten replays on every
 * switch and reads as a flash.
 *
 * Nothing actually transitioned — the composer was born focused — so we hold
 * `transition-none` across the mount window and let it paint focused from the
 * first frame. Enabling the transition later never retroactively animates a
 * style already applied, so hover and blur behave normally from then on.
 *
 * A timer rather than rAF: React flushes passive effects (where Lexical
 * focuses) on its own schedule relative to paint, so a frame count isn't a
 * reliable "focus has landed" signal.
 */
function useTransitionSettled(): boolean {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setSettled(true), MOUNT_SETTLE_MS);
    return () => window.clearTimeout(id);
  }, []);
  return settled;
}

export interface HomeComposerProps {
  /** Workspace the chat is started in. Undefined = the user's private workspace. */
  workspaceId?: string;
  workspaceName?: string;
  workspaceIcon?: MetadataIcon | null;
}

/**
 * Home composer: the shared MessageInput wired to start a chat. Sending creates
 * the flow session, posts the message, then lands on the full chat page
 * (`/chat/:flowSessionId`).
 *
 * File-drop targeting differs by scope. Unscoped, the composer claims OS drops
 * for the whole page (there is nowhere else for a file to go). Scoped, the
 * workspace page around it uploads dropped files into the workspace, so the
 * composer instead nests its own drop zone: drop on the input to attach,
 * anywhere else to upload. Registering the page-wide handler here would also
 * re-enable `OsFileDropLayer`, which *broadcasts* to every registered callback
 * — a drop would then attach AND upload.
 */
export function HomeComposer({
  workspaceId,
  workspaceName,
  workspaceIcon,
}: HomeComposerProps = {}) {
  const navigate = useNavigate();
  const { setCurrentSessionId } = useSessionContext();
  const [referencedEntities, setReferencedEntities] = useState<
    ReferencedEntity[]
  >([]);
  const messageInputRef = useRef<MessageInputRef>(null);
  const lg = useBreakpoint("lg");
  const scoped = !!workspaceId;
  const transitionSettled = useTransitionSettled();

  // Unscoped, the backend files a new chat in the user's private workspace
  // (see the `workspaceId` prop note), so surface that scope the same way the
  // workspace pages surface theirs — "in My Workspace" — instead of leaving
  // the toolbar blank about where the chat lands.
  const privateWorkspace = usePrivateWorkspace();
  const scopeChip = scoped ? (
    workspaceName ? (
      <ComposerScopeChip
        name={workspaceName}
        icon={workspaceIcon}
        currentWorkspaceId={workspaceId}
      />
    ) : undefined
  ) : (
    <ComposerScopeChip
      name="My Workspace"
      icon={privateWorkspace?.metadata?.icon}
      /* Unscoped chats land in the private workspace, so that's the chip
         popover's checked row (its list has no Home entry), and selecting
         it keeps you on `/` — reaffirming the current target isn't a
         navigation. Any other workspace navigates to its page. */
      currentWorkspaceId={privateWorkspace?.id}
    />
  );

  const {
    uploadedFiles,
    onAttachFile,
    onAttachFileRecords,
    onRemoveFile,
    onSendMessage,
    ensureFlowSession,
    isLoading,
  } = useMessageActions({
    workspaceId,
    onFlowSessionCreated: () => {
      analytics.trackNewChatCreated();
    },
    onMessageSent: ({ flowSession }) => {
      navigate(route(ROUTES.CHAT.DETAIL, { flowSessionId: flowSession.id }));
      // Reset so the composer starts a fresh chat if the user comes back.
      setCurrentSessionId(undefined);
    },
  });

  // Unscoped only: attach files dropped anywhere on the home page
  // (WorkspaceLayout provides the OsFileDropLayer this subscribes to).
  useRegisterOsFileDrop(scoped ? undefined : onAttachFile);

  // In-app dnd-kit drags (browser rows, rail explorer) land on the
  // `composer-attach` surface wrapping the input below: mint the pending
  // chat's session, copy the records in, enqueue them as attachments.
  useRegisterComposerAttachTarget({
    ensureSession: ensureFlowSession,
    onAttached: onAttachFileRecords,
  });

  const handleSend = async (params: PostMessageParams) => {
    const content = params.content?.trim();
    // Attachment-only sends are valid — MessageInput enables Send once
    // attachments finish uploading, and the ready file ids merge inside
    // useMessageActions (the send payload itself carries none). Only a
    // truly empty message is a no-op (backstop; MessageInput already
    // disables Send in that case).
    if (!content && uploadedFiles.length === 0) {
      return;
    }

    await onSendMessage({
      id: uuidv4(),
      content,
      file_record_ids: params.file_record_ids,
      referenced_entities: referencedEntities,
    });

    analytics.trackHomeMessageSent({
      content,
      fileRecordIds: params.file_record_ids ?? [],
    });
  };

  const composer = (
    /** In-app drag target wraps ONLY the input: drop on the composer to
     *  attach, anywhere else on the page to upload/move (the page's own
     *  targets). Distinct from the native-OS WorkspaceDropZone below. */
    <ComposerAttachDropTarget
      scopeKey={workspaceId ?? "home"}
      className="w-full rounded-xl"
    >
      {/* Elevation only — ComposerShell owns the framed surface (border +
       *  rust focus ring); a second frame would double-border the input. */}
      <div className="w-full rounded-xl shadow-elev-1">
        <MessageInput
          ref={messageInputRef}
          sendButtonDisabled={isLoading}
          onSend={handleSend}
          onAttachFile={onAttachFile}
          onRemoveFile={onRemoveFile}
          attachedFiles={uploadedFiles}
          onReferencedEntitiesChange={setReferencedEntities}
          autoFocus={lg}
          location="main"
          controlSet={MessageInputControlSet.DEFAULT}
          /* Sits immediately right of the skills button, left of the send
           cluster — so the composer's height never changes with scope. */
          middleToolbarSlot={scopeChip}
          classNames={{
            container: "w-full",
            editor: "min-h-[72px]",
            // `transition-none` drops ComposerShell's transition for the mount
            // window — see useTransitionSettled.
            contentWrapper: cn(!transitionSettled && "transition-none"),
          }}
        />
      </div>
    </ComposerAttachDropTarget>
  );

  if (!scoped) return composer;

  return (
    <WorkspaceDropZone
      compact
      label="Drop files to attach to your message"
      className="rounded-xl"
      // Chat attachments have no folder concept — flatten any dropped tree.
      onDrop={({ files, tree }) =>
        onAttachFile([...files, ...(tree?.files ?? []).map((f) => f.file)])
      }
    >
      {composer}
    </WorkspaceDropZone>
  );
}
